import Header from "@/components/header";
import { MerchantUserDto } from "@/models/club";
import ChairRental from "@/modules/team/chairRental";
import HeaderSection from "@/modules/team/add/headerSection";
import MemberType from "@/modules/team/add/memberType";
import TeamMemberProfileInformation from "@/modules/team/add/profileInformation";
import useGetMerchantUserById from "@/services/merchantUser/hooks/useGetMerchantUserById";
import useUpdateMerchantUser from "@/services/merchantUser/hooks/useUpdateMerchantUser";
import {
  CreateUserPayloadForm,
  merchantUserTypeMap,
} from "@/services/merchantUser/type";
import { useGetImage } from "@/utilities/helper/getImage";
import {
  Button,
  Card,
  Container,
  LoadingOverlay,
  Stack,
  Title,
} from "@mantine/core";
import { yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import moment from "moment-jalaali";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import {
  UserFormProvider,
  useUserForm,
  useUserFormContext,
} from "../add/Context";
import useIsSmall from "@/hooks/useIsSmall";

const isValidCommission = (value?: string | number | null) =>
  value !== undefined && value !== null && value !== "" && Number(value) >= 0 && Number(value) <= 100;

const schema = Yup.object().shape({
  FirstName: Yup.string().required("نام الزامی است"),
  LastName: Yup.string().required("نام خانوادگی الزامی است"),
  MobileNumber: Yup.string().required("شماره تلفن الزامی است"),
  // 0 is a valid commission (e.g. owner or salaried staff); the backend stores 0–100.
  markupPercentageFrom: Yup.string()
    .required("درصد کمیسیون را وارد نمایید (۰ تا ۱۰۰)")
    .test(
      "commission-range",
      "درصد کمیسیون باید بین ۰ تا ۱۰۰ باشد",
      (value) => isValidCommission(value)
    ),
  // password: Yup.string().required("رمز عبور الزامی است"),
  // merchantUserType: Yup.string().required("نوع کاربر الزامی است"),
});

function EditTeamMemberContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromQuery = searchParams.get("tab") || "1";
  const [active, setActive] = useState<string>(tabFromQuery);
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const form = useUserFormContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading } = useGetMerchantUserById(Number(id));
  const user = data as MerchantUserDto;
  const { imageUrl } = useGetImage({ image: user?.image });
  const didPrefillImageRef = useRef(false);
  const isSmall = useIsSmall();
  // Update query params when active tab changes
  useEffect(() => {
    setSearchParams({ tab: active }, { replace: true });
  }, [active, setSearchParams]);

  useEffect(() => {
    if (user) {
      form?.setValues({
        FirstName: user?.firstName || "",
        LastName: user?.lastName || "",
        MobileNumber: user?.phoneNumber || "",
        genderForm: user?.gender?.toString() || "",
        day: moment(user?.birthDate).locale("fa").format("jDD"),
        month: moment(user?.birthDate).locale("fa").format("jMM"),
        year: moment(user?.birthDate).locale("fa").format("jYYYY"),
        CalendarColor: user?.calendarColor || "",
        merchantUserTypeForm:
          user?.merchantUserType === 1
            ? "Manager"
            : user?.merchantUserType === 0
              ? "Admin"
              : "Barber",
        file: user?.image?.fileName
          ? new File([], user.image.fileName)
          : undefined,
        services: user?.merchantServices || [],
        JobTitle: user?.jobTitle || "",
        ShowOnWebsite: user?.showOnWebsite,
        markupPercentageFrom: user?.markupPercentage?.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Prefill profile image preview exactly once when imageUrl becomes available
  useEffect(() => {
    if (!didPrefillImageRef.current && imageUrl) {
      form.setValues({
        profileImage: [
          {
            data_url: imageUrl,
            file: undefined,
          },
        ],
      });
      didPrefillImageRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  const { mutate: updateMerchantUser, isLoading: isUpdatingMerchantUser } =
    useUpdateMerchantUser();

  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current) {
        const rect = stickyRef.current.getBoundingClientRect();
        // Check if the element is stuck (top position is at or below 56px - header height)
        setIsSticky(rect.top <= 56);
      }
    };

    // Try to find the scrollable container
    const scrollableContainer = document.querySelector(
      "#dashboard-layout"
    ) as HTMLElement;

    if (scrollableContainer) {
      scrollableContainer.addEventListener("scroll", handleScroll);
      return () =>
        scrollableContainer.removeEventListener("scroll", handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const submit = (values: CreateUserPayloadForm) => {
    console.log(values);

    if (form.values.merchantUserTypeForm === undefined) {
      notifications.show({
        message: "نوع کاربر را انتخاب کنید",
        color: "yellow",
      });
      setActive("3");
      return;
    }
    updateMerchantUser(
      {
        MerchantUserId: user?.id,
        FirstName: values.FirstName,
        LastName: values.LastName,
        // ServiceIds: values?.services?.map((service) => service.id ?? 0) || [],
        Gender: Number(values?.genderForm),
        BirthDate: (() => {
          const y = values?.year?.toString() || "";
          const m = values?.month?.toString() || "";
          const d = values?.day?.toString() || "";
          if (!y || !m || !d) return "";
          const jalali = `${y}/${m}/${d}`;
          const parsed = moment(jalali, "jYYYY/jMM/jDD", true);
          return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        })(),
        CalendarColor: values?.CalendarColor,
        MerchantUserType:
          merchantUserTypeMap[
          form.values.merchantUserTypeForm as keyof typeof merchantUserTypeMap
          ],
        // Only send file if user changed the image (file will be defined)
        file: values?.profileImage![0]?.file || undefined,
        "UserInfo.UserId": undefined,
        "UserInfo.MobileNumber": "",
        JobTitle: values?.JobTitle,
        ShowOnWebsite: values?.ShowOnWebsite ?? true,
        MarkupPercentage: Number(values?.markupPercentageFrom)
      },
      {
        onSuccess() {
          notifications.show({
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          navigate("/dashboard/teams/teamMembers");
        },
      }
    );
  };

  useEffect(() => {

    if (Object.keys(form.errors).length > 0) {
      setActive("1");
    }
  }, [form.errors]);

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Header
        title="تیم"
        showBack
        route="/dashboard/teams/teamMembers"
      />
      <Container
        w="100%"
        size="md"
        bg="white"
        mb={isSmall ? "100px" : "50px"}
        px="0"
      >
        <Title order={4} pb="0" px="md">
          ویرایش عضو تیم
        </Title>
        <Stack justify="start" flex={1}>
          <Stack
            ref={stickyRef}
            gap="lg"
            py="sm"
            styles={{
              root: {
                position: "sticky",
                top: "56px", // Header height to stick below the header
                zIndex: 15, // Higher than header but still allows proper stacking
                backgroundColor: "white",
                borderBottom: isSticky
                  ? "1px solid var(--mantine-color-gray-2)"
                  : "none",
                transition: "border-bottom 0.2s ease",
              },
            }}
          >
            <HeaderSection active={active} setActive={setActive} hideService />
          </Stack>
          <form
            onSubmit={form.onSubmit(submit)}
            name="edit-customer"
            className="w-100"
            id="edit-customer"
          >
            <Stack px="md">
              {active === "1" && <TeamMemberProfileInformation readonly />}

              {active === "3" && <><MemberType commissionLocked={Boolean((user as { isChairRenter?: boolean } | undefined)?.isChairRenter)} /><ChairRental user={user} /></>}
            </Stack>
          </form>
        </Stack>
      </Container>
      {isSmall ? (
        <Card
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
            },
          }}
          withBorder
          radius={0}
        >
          <Button
            type="submit"
            size="lg"
            fullWidth

            form="edit-customer"
            loading={isUpdatingMerchantUser}
          >
            ویرایش
          </Button>
        </Card>
      ) : (
        <Container w="100%" size="md" bg="white" mb="100px" px="0">
          <Button
            type="submit"
            size="lg"
            fullWidth

            form="edit-customer"
            loading={isUpdatingMerchantUser}
          >
            ویرایش
          </Button>
        </Container>
      )}
    </>
  );
}

function EditTeamMember() {
  const form = useUserForm({
    mode: "controlled",
    validate: yupResolver(schema),
    initialValues: {
      FirstName: "",
      LastName: "",
      MerchantUserType: undefined,
      file: undefined,
      services: [],
      day: "",
      month: "",
      year: "",
      profileImage: [],
      BirthDate: undefined,
      CalendarColor: "",
      Gender: undefined,
      genderForm: "",
      // mobileNumber: "",
      MarkupPercentage: 0
    },
  });

  return (
    <UserFormProvider form={form}>
      <EditTeamMemberContent />
    </UserFormProvider>
  );
}

export default EditTeamMember;
